import { DocumentIcon, EyeIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

import { IContact } from "../../models/Contact";
import LoadingWheel from "../shared/LoadingWheel";
import { IAccount } from "../../models/account/IAccount";
import useAccountContacts from "../../data/useAccountContacts";
import { AccountContactModal } from "./AccountContactModal";
import { useState } from "react";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { AccountContactAddModal } from "./AccountContactAddModal";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import useContacts from "../../data/useContacts";
import { useQueryClient } from "@tanstack/react-query";
import { ContactCreateEditModal } from "../contacts/ContactCreateEditModal";
import ContactDocumentsModal from "../contacts/ContactDocumentsModal";
import openInNewTab from "../../utils/openInNewTab";

interface AccountContactRowProps {
  contact: IContact;
  isPrimaryContact: boolean;
  onClick: (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => void;
}

function AccountContactListRow({ contact, isPrimaryContact = false, onClick }: AccountContactRowProps) {
  const { t } = useTranslation("accounts");
  const queryClient = useQueryClient();
  const { update } = useContacts();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [documentsOpen, setDocumentsOpen] = useState(false);

  const handleRemoveContact = () => {
    const accountId = contact.accountId;
    update.mutate(
      // Leaving accountId out no longer unlinks a contact; removing it has to be asked for.
      { ...contact, accountId: undefined, removeFromAccount: true },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["accounts", accountId, "contacts"],
          });
          setDeleteOpen(false);
        },
      }
    );
  }

  return (
    <>
      <ConfirmModal open={deleteOpen} setOpen={setDeleteOpen} isLoading={false}
        title={t("Remove From Account")}
        message={t("Are you sure you want to remove this contact from this account?")}
        confirmButtonText={t("Remove")}
        onConfirm={handleRemoveContact}
      />
      {/* Mounted only while open — the modal's hook eagerly fetches the contact's
          documents, so keeping it mounted per row fired one fetch per contact. */}
      {documentsOpen && (
        <ContactDocumentsModal open={documentsOpen} setOpen={setDocumentsOpen} contactId={contact.id} />
      )}
      <tr onClick={onClick} className="hover:cursor-pointer hover:bg-gray-100">
        <td className="px-3 py-3 border text-sm">{contact.fullname}</td>
        <td className="px-3 py-3 border hidden sm:table-cell text-sm">
          {contact.email}
        </td>
        <td className="px-3 py-3 border text-sm">{contact.telephone1}</td>
        <td className="px-3 py-3 border hidden sm:table-cell text-sm">
          {contact.telephone2}
        </td>
        <td className="px-3 py-3 border hidden sm:table-cell text-center text-sm">
          {isPrimaryContact && (
            <span className="px-3 py-1 rounded-full font-bold bg-blue-100 text-blue-500">
              {t("Primary")}
            </span>
          )}
        </td>
        <td className="px-3 py-3 border text-sm">
          <div className="px-3 flex items-center space-x-2">
            <PencilIcon className="w-5 h-5 text-gray-600" />
            {/* The contact page - every lead on them and, for admins, merging - which has no menu link. */}
            <span title={t("Open contact page")}>
              <ButtonInput onClick={(e: any) => { e.stopPropagation(); openInNewTab(`/contacts/${contact.id}`); }} classes={undefined} >
                <EyeIcon className="w-5 h-5 text-primary-600 hover:text-primary-700" />
              </ButtonInput>
            </span>
            <ButtonInput onClick={(e: any) => { e.stopPropagation(); setDocumentsOpen(true); }} classes={undefined} >
              <DocumentIcon className={`w-5 h-5 ${contact.documents?.length > 0 ? "text-green-600 hover:text-green-700" : "text-amber-600 hover:text-amber-700"}`} />
            </ButtonInput>
            <ButtonInput onClick={(e: any) => { e.stopPropagation(); setDeleteOpen(true); }} classes={undefined} >
              <TrashIcon className="w-5 h-5 text-red-600 hover:text-red-700" />
            </ButtonInput>
          </div>
        </td>
      </tr>
    </>
  );
}

export default function AccountContactList({ account }: { account: IAccount }) {
  const { t } = useTranslation("accounts");
  const { accountContacts } = useAccountContacts(account.id, "AccountContactList");
  const [selectedContact, setSelectedContact] = useState<IContact>();
  const [viewOpen, setViewOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  if (accountContacts.isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <LoadingWheel />
      </div>
    );
  } else if (
    accountContacts.isError ||
    !accountContacts.data ||
    !accountContacts.data.data
  ) {
    return (
      <div className="flex items-center justify-center py-10">
        <p className="text-red-600 font-bold text-xl">
          {t("Error loading addresses")}
        </p>
      </div>
    );
  } else {
    return (
      <>
        <ContactCreateEditModal
          contact={selectedContact}
          open={viewOpen}
          setOpen={setViewOpen}
        />
        <AccountContactAddModal
          accountId={account.id}
          open={addOpen}
          setOpen={setAddOpen}
        />
        <div>
          <div className="p-2 sm:p-4 flex justify-end">
            <ButtonInput
              label={t("Add Contact")}
              onClick={() => setAddOpen(true)}
              isSubmit={false}
              classes=""
            />
          </div>
          <div className="sm:hidden p-2">
            <p className="text-center">{t("Tap a row to see the full record")}</p>
          </div>
          <table className="min-w-full divide-y border-collapse divide-gray-300">
            <thead>
              <tr className="bg-primary-50 text-gray-800">
                <th className="py-3.5 px-3 text-sm font-semibold text-left">
                  {t("Name")}
                </th>
                <th className="py-3.5 px-3 text-sm font-semibold hidden sm:table-cell text-left">
                  {t("Email")}
                </th>
                <th className="py-3.5 px-3 text-sm font-semibold text-left">
                  {t("Phone 1")}
                </th>
                <th className="py-3.5 px-3 text-sm font-semibold hidden sm:table-cell text-left">
                  {t("Phone 2")}
                </th>
                <th className="py-3.5 px-3 text-sm font-semibold hidden sm:table-cell text-left">
                  {t("Assignment")}
                </th>
                <th className="py-3.5 px-3 text-sm font-semibold text-left">
                  {t("Actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {accountContacts.data.data.map((contact) => (
                <AccountContactListRow
                  key={contact.id}
                  contact={contact}
                  isPrimaryContact={contact.id === account.primaryContactId}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedContact(contact);
                    setViewOpen(true);
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  }
}
